/**
 * DIN (Document Intake Note) field normalizer — SINGLE SOURCE OF TRUTH.
 *
 * Every DIN creation path must derive its title, dates and scalar money columns
 * from this module so that a given slip produces an identical DIN regardless of
 * whether it arrived via SmartTask capture, WhatsApp, an upload link, or the
 * manual document slide-over.
 *
 * Canonical rules:
 * - title            = "<AI vendor name> <D Mon YYYY>" using the TRANSACTION date
 *                      (the date printed on the slip). Never the file name when a
 *                      vendor is known.
 * - transaction_date = the date on the slip (persisted to work_items.expense_date)
 * - intake_date      = the moment the slip was scanned (persisted to work_items.issued_at)
 * - When the slip date is missing or implausible we fall back to the intake date
 *   and flag it via `transaction_date_inferred` so the UI can prompt for review.
 *
 * Dates are handled as calendar dates (no timezone component) to avoid the
 * SAST(+2) off-by-one that produced titles a day behind the printed slip date.
 */

import { parseLocaleAmount } from '@/lib/money';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A slip date more than this many days in the future is treated as an OCR error.
 * Small allowance covers device clock skew and timezone edges.
 */
const MAX_FUTURE_DAYS = 2;

/**
 * A slip date older than this many years is treated as an OCR error. This is what
 * catches the classic "2026 read as 2014" misread on faded thermal receipts.
 */
const MAX_AGE_YEARS = 10;

export interface DinExtractionLike {
  vendor_name?: unknown;
  vendor?: unknown;
  supplier_name?: unknown;
  receipt_date?: unknown;
  date?: unknown;
  expense_date?: unknown;
  transaction_date?: unknown;
  invoice_date?: unknown;
  receipt_number?: unknown;
  invoice_number?: unknown;
  subtotal?: unknown;
  tax_amount?: unknown;
  vat_amount?: unknown;
  total_amount?: unknown;
  amount?: unknown;
  round_off?: unknown;
  currency?: unknown;
  line_items?: unknown;
  confidence?: unknown;
}

export interface NormalizeDinOptions {
  /** Extraction payload from geminiVision (or an equivalently shaped object). */
  extraction?: DinExtractionLike | null;
  /** Supplier name resolved by contact matching — used only if AI found no vendor. */
  contactName?: string | null;
  /** Human sender (WhatsApp contact, uploader) — fallback before file name. */
  senderName?: string | null;
  /** Original file name — last-resort title fallback. */
  fileName?: string | null;
  /** When the document was received/scanned. Defaults to now. */
  intakeDate?: Date | string | null;
  /** Explicit transaction date that overrides the extraction (user-edited value). */
  transactionDateOverride?: string | null;
  /** Fallback label when nothing else is known (e.g. "Expense capture"). */
  fallbackLabel?: string | null;
}

export interface NormalizedDinFields {
  /** Canonical DIN title: "<vendor> <D Mon YYYY>". */
  title: string;
  /** Vendor name used for the title, or null when unknown. */
  vendor_name: string | null;
  /** Calendar date on the slip, "YYYY-MM-DD", or null when unknown. */
  transaction_date: string | null;
  /** True when transaction_date was derived from the intake date, not the slip. */
  transaction_date_inferred: boolean;
  /** Reason the slip date was rejected, for surfacing in the inbox. */
  transaction_date_issue: 'missing' | 'unparseable' | 'future' | 'too_old' | null;
  /** ISO timestamp of when the document was scanned/received. */
  intake_date: string;
  receipt_number: string | null;
  currency: string;
  subtotal: number;
  tax_total: number;
  grand_total: number;
  rounding_adjustment: number;
}

function firstNonEmptyString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
}

function toFiniteNumber(value: unknown): number | null {
  return parseLocaleAmount(value);
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Parse a date into calendar parts WITHOUT applying a timezone shift.
 *
 * `new Date('2026-06-15')` is parsed as UTC midnight, which renders as the 14th
 * in SAST-negative offsets and, more importantly, round-trips into a timestamp
 * column as `2026-06-14T22:00:00Z`. We therefore read Y/M/D textually whenever
 * the input looks like a plain date.
 */
function parseCalendarDate(value: unknown): { year: number; month: number; day: number } | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }

  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Fast path: YYYY-MM-DD, optionally followed by a time component we ignore.
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { year, month, day };
    }
    return null;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return { year: parsed.getFullYear(), month: parsed.getMonth() + 1, day: parsed.getDate() };
}

function formatCalendarDate(parts: { year: number; month: number; day: number }): string {
  const mm = String(parts.month).padStart(2, '0');
  const dd = String(parts.day).padStart(2, '0');
  return `${parts.year}-${mm}-${dd}`;
}

/**
 * Try to extract a date from receipt raw text when OCR failed to parse it.
 * Handles common OCR mangling like "0308/2026" (missing slash from "03/08/2026").
 */
export function extractDateFromRawText(rawText: string | undefined | null): string | null {
  if (!rawText) return null;

  // Standard format: "Date: 03/08/2026" or "Date: 03 08 2026"
  const standardMatch = rawText.match(/Date:\s*(\d{1,2})[\/\s](\d{1,2})[\/\s](\d{4})/i);
  if (standardMatch) {
    const day = Number(standardMatch[1]);
    const month = Number(standardMatch[2]);
    const year = Number(standardMatch[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  // OCR-mangled: "Date: 0308/2026" (missing slash from "03/08/2026")
  const mangledMatch = rawText.match(/Date:\s*(\d{2})(\d{2})\/(\d{4})/i);
  if (mangledMatch) {
    const day = Number(mangledMatch[1]);
    const month = Number(mangledMatch[2]);
    const year = Number(mangledMatch[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  return null;
}

/**
 * Render the date portion of a DIN title: "15 Jun 2026".
 */
export function formatDinTitleDate(value: unknown): string | null {
  const parts = parseCalendarDate(value);
  if (!parts) return null;
  return `${parts.day} ${MONTH_NAMES[parts.month - 1]} ${parts.year}`;
}

/**
 * Build the canonical DIN title. Exported so read paths can regenerate a title
 * for legacy rows without duplicating the format.
 */
export function buildDinTitle(vendorName: string | null, dateValue: unknown, fallback: string): string {
  const vendor = vendorName && vendorName.trim() ? vendorName.trim() : null;
  const datePart = formatDinTitleDate(dateValue);
  if (vendor && datePart) return `${vendor} ${datePart}`;
  if (vendor) return vendor;
  // Even without a vendor, include the date so inbox rows are never dateless
  if (datePart) return `${fallback} ${datePart}`;
  return fallback;
}

/**
 * Decide which calendar date represents the transaction, rejecting implausible
 * OCR results and falling back to the intake date.
 */
function resolveTransactionDate(
  candidate: unknown,
  intake: Date,
): { date: string; inferred: boolean; issue: NormalizedDinFields['transaction_date_issue'] } {
  const intakeDateString = formatCalendarDate({
    year: intake.getFullYear(),
    month: intake.getMonth() + 1,
    day: intake.getDate(),
  });

  const hasCandidate = candidate != null && String(candidate).trim() !== '';
  if (!hasCandidate) {
    return { date: intakeDateString, inferred: true, issue: 'missing' };
  }

  const parts = parseCalendarDate(candidate);
  if (!parts) {
    return { date: intakeDateString, inferred: true, issue: 'unparseable' };
  }

  // Compare as UTC epoch days so the plausibility window is timezone-independent.
  const candidateUtc = Date.UTC(parts.year, parts.month - 1, parts.day);
  const intakeUtc = Date.UTC(intake.getFullYear(), intake.getMonth(), intake.getDate());
  const dayDelta = (candidateUtc - intakeUtc) / 86_400_000;

  if (dayDelta > MAX_FUTURE_DAYS) {
    return { date: intakeDateString, inferred: true, issue: 'future' };
  }
  if (dayDelta < -(MAX_AGE_YEARS * 365.25)) {
    return { date: intakeDateString, inferred: true, issue: 'too_old' };
  }

  return { date: formatCalendarDate(parts), inferred: false, issue: null };
}

/**
 * Fill in a missing `total_amount` from subtotal + tax, or from the sum of line
 * item totals. Mirrors the fallback that `/api/v1/work-items/validate-receipt`
 * has always applied, so the server-side intake endpoints no longer produce
 * zero-amount DINs for slips where Gemini omitted the grand total.
 *
 * Mutates and returns the payload for convenience.
 */
export function reconcileExtractionTotals(payload: Record<string, unknown>): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return payload;

  if (toFiniteNumber(payload.total_amount) != null) return payload;

  const subtotal = toFiniteNumber(payload.subtotal);
  const tax = toFiniteNumber(payload.tax_amount);
  if (subtotal != null && tax != null) {
    payload.total_amount = round2(subtotal + tax);
    return payload;
  }

  const lineItems = payload.line_items;
  if (Array.isArray(lineItems) && lineItems.length > 0) {
    const sum = lineItems.reduce<number>((acc, item) => {
      const total = toFiniteNumber((item as Record<string, unknown> | null)?.total);
      return acc + (total ?? 0);
    }, 0);
    if (sum > 0) {
      payload.total_amount = round2(sum);
      return payload;
    }
  }

  if (subtotal != null) {
    payload.total_amount = round2(subtotal);
  }

  return payload;
}

/**
 * Derive every DIN field that must be identical across intake channels.
 */
export function normalizeDinFields(options: NormalizeDinOptions): NormalizedDinFields {
  const extraction = (options.extraction || {}) as DinExtractionLike;

  const intake = options.intakeDate ? new Date(options.intakeDate) : new Date();
  const intakeDate = Number.isNaN(intake.getTime()) ? new Date() : intake;

  const vendorName = firstNonEmptyString(
    extraction.vendor_name,
    extraction.vendor,
    extraction.supplier_name,
    options.contactName,
  );

  const rawTransactionDate = options.transactionDateOverride
    ?? firstNonEmptyString(
      extraction.receipt_date,
      extraction.transaction_date,
      extraction.date,
      extraction.expense_date,
      extraction.invoice_date,
    );

  const resolved = resolveTransactionDate(rawTransactionDate, intakeDate);

  const fallbackTitle = firstNonEmptyString(
    options.senderName,
    options.fileName,
    options.fallbackLabel,
  ) || 'Document intake';

  // The title always carries the transaction date — including when it was
  // inferred from the intake date — so inbox rows are never dateless.
  const title = buildDinTitle(vendorName, resolved.date, fallbackTitle);

  // Money: derive from receipt-level totals so that
  // subtotal + tax_total + rounding_adjustment === grand_total exactly.
  const grandTotal = round2(toFiniteNumber(extraction.total_amount) ?? toFiniteNumber(extraction.amount) ?? 0);
  const taxTotal = round2(toFiniteNumber(extraction.tax_amount) ?? toFiniteNumber(extraction.vat_amount) ?? 0);
  const roundOff = toFiniteNumber(extraction.round_off) ?? 0;

  const extractedSubtotal = toFiniteNumber(extraction.subtotal);
  const subtotal = extractedSubtotal != null
    ? round2(extractedSubtotal)
    : round2(grandTotal - taxTotal - roundOff);

  const roundingAdjustment = round2(grandTotal - subtotal - taxTotal);

  return {
    title,
    vendor_name: vendorName,
    transaction_date: resolved.date,
    transaction_date_inferred: resolved.inferred,
    transaction_date_issue: resolved.issue,
    intake_date: intakeDate.toISOString(),
    receipt_number: firstNonEmptyString(extraction.receipt_number, extraction.invoice_number),
    currency: firstNonEmptyString(extraction.currency) || 'ZAR',
    subtotal,
    tax_total: taxTotal,
    grand_total: grandTotal,
    rounding_adjustment: roundingAdjustment,
  };
}
